/**
 * Presentation copy for the setup audit: which check area each issue belongs
 * to, what it breaks (in one line, shared by everyone in the group), and how
 * to fix it. The audit itself (keys, severities, per-person detail) lives in
 * ../SetupHealth.tsx and is unchanged by anything here.
 */
import type { Employee } from '../../../store/slices/employeeSlice';
import type { AuditedEmployee, IssueSeverity, SetupIssue } from '../SetupHealth';
import { ACCESS_TAB_LABEL } from '../../../utils/settingsTabs';

export type CheckAreaKey = 'records' | 'manager' | 'jr-ae' | 'login' | 'allotments' | 'wfh';

/** The Settings tab where roles and logins are managed (shared with AdminPanel's tab label). */
export { ACCESS_TAB_LABEL };

/** Where an issue is fixed: the employee record, a manager-link sync, or the access tab. */
export type FixKind = 'edit' | 'sync' | 'access';

export interface CheckArea {
  key: CheckAreaKey;
  label: string;
  description: string;
}

export const CHECK_AREAS: CheckArea[] = [
  { key: 'records', label: 'Employee records', description: 'Name, email, role, department and team' },
  { key: 'manager', label: 'Manager routing', description: 'Who sees and approves their time off' },
  { key: 'jr-ae', label: 'Jr manager assignment', description: 'Only for employees with a Jr manager' },
  { key: 'login', label: 'Logins', description: 'Their own sign-in, requests and balances' },
  { key: 'allotments', label: 'Time-off allotments', description: 'PTO, sick time and start date' },
  { key: 'wfh', label: 'WFH escalation', description: 'VP approval when they are not hybrid' },
];

export const areaLabel = (key: CheckAreaKey) => CHECK_AREAS.find((area) => area.key === key)?.label ?? key;

export interface IssueGuide {
  area: CheckAreaKey;
  /** What this breaks, for everyone in the group. */
  summary: string;
  /** How to fix it, as an instruction. */
  fix: string;
  fixKind: FixKind;
  /** The per-person detail names specific people or values worth showing on each row. */
  specific?: boolean;
}

const ISSUE_GUIDE: Record<string, IssueGuide> = {
  // Employee records
  'no-name': {
    area: 'records',
    summary: 'The record has no name, so manager links and VP escalation can’t match it.',
    fix: 'Add their name in the employee record.',
    fixKind: 'edit',
  },
  'no-email': {
    area: 'records',
    summary: 'Approval results, reminders and summaries have nowhere to go.',
    fix: 'Add a work email in the employee record.',
    fixKind: 'edit',
  },
  'bad-email': {
    area: 'records',
    summary: 'The email isn’t a valid address, so notification emails bounce.',
    fix: 'Correct the email in the employee record.',
    fixKind: 'edit',
    specific: true,
  },
  'dup-email': {
    area: 'records',
    summary: 'Another active record uses the same email, so time off and logins can attach to the wrong record.',
    fix: 'Give each record its own email, or deactivate the duplicate record.',
    fixKind: 'edit',
  },
  'no-role': {
    area: 'records',
    summary: 'Role is blank. The employee form requires it and the directory shows an empty chip.',
    fix: 'Set their role in the employee record.',
    fixKind: 'edit',
  },
  'no-department': {
    area: 'records',
    summary: 'They’re missing from department views, coverage counts and reports.',
    fix: 'Assign a department in the employee record.',
    fixKind: 'edit',
  },
  'no-team': {
    area: 'records',
    summary: 'Team views and weekend shift grouping skip them.',
    fix: 'Set their team in the employee record.',
    fixKind: 'edit',
  },

  // Manager routing
  'no-manager': {
    area: 'manager',
    summary: 'Their time-off requests don’t appear on any manager dashboard, and approval emails are skipped.',
    fix: 'Pick their manager in the employee record.',
    fixKind: 'edit',
  },
  'manager-link-broken': {
    area: 'manager',
    summary: 'The linked manager account no longer exists.',
    fix: 'Re-select the manager in the employee record.',
    fixKind: 'edit',
  },
  'manager-no-role': {
    area: 'manager',
    summary: 'The linked manager can approve from email but can’t open the manager dashboard or time-off pages.',
    fix: `Grant the manager the Manager role in ${ACCESS_TAB_LABEL}.`,
    fixKind: 'access',
    specific: true,
  },
  'manager-ambiguous': {
    area: 'manager',
    summary: 'Several employees share the manager’s name, so the approval-email lookup fails and no email is sent.',
    fix: 'Pick the manager account in the employee record.',
    fixKind: 'edit',
    specific: true,
  },
  'manager-name-inexact': {
    area: 'manager',
    summary: 'The manager name differs in case or spacing, so approval emails aren’t sent. Requests surface only if the manager checks their dashboard.',
    fix: 'Fix the spelling, or pick the manager account in the employee record.',
    fixKind: 'edit',
    specific: true,
  },
  'manager-queue-gap': {
    area: 'manager',
    summary: 'Matched by name only. Emails and the manager dashboard work, but the time-off approval pages don’t route their requests.',
    fix: 'Sync manager links, or pick the manager account in the employee record.',
    fixKind: 'sync',
    specific: true,
  },
  'manager-name-only': {
    area: 'manager',
    summary: 'Matched by name only. Routing works while names stay identical and breaks silently if they drift.',
    fix: 'Sync manager links, or pick the manager account in the employee record.',
    fixKind: 'sync',
    specific: true,
  },
  'manager-profile-ambiguous': {
    area: 'manager',
    summary: 'The manager name matches more than one login account. A sync would link one of them at random.',
    fix: 'Pick the correct manager account in the employee record. Don’t sync for these.',
    fixKind: 'edit',
    specific: true,
  },
  'manager-not-synced': {
    area: 'manager',
    summary: 'The manager name matches a login account that isn’t linked yet, so time off doesn’t reach them.',
    fix: 'Sync manager links to write the missing link.',
    fixKind: 'sync',
    specific: true,
  },
  'manager-unrecognized': {
    area: 'manager',
    summary: 'The manager name doesn’t match any employee or login account, so time off doesn’t reach a manager.',
    fix: 'Pick their manager in the employee record.',
    fixKind: 'edit',
    specific: true,
  },
  'manager-mismatch': {
    area: 'manager',
    summary: 'The manager name and the linked account point to different people, so time off can reach the wrong manager.',
    fix: 'Correct whichever one is wrong in the employee record.',
    fixKind: 'edit',
    specific: true,
  },
  'manager-name-blank': {
    area: 'manager',
    summary: 'A manager account is linked but the name is empty, so the directory and name matching show nothing.',
    fix: 'Re-select the manager in the employee record to fill in the name.',
    fixKind: 'edit',
  },
  'manager-name-email': {
    area: 'manager',
    summary: 'The manager name holds an email address, which breaks name matching on the manager dashboard.',
    fix: 'Replace it with the manager’s full name in the employee record.',
    fixKind: 'edit',
    specific: true,
  },
  'manager-inactive': {
    area: 'manager',
    summary: 'Their manager is no longer active, so requests route to someone who has left.',
    fix: 'Assign a current manager in the employee record.',
    fixKind: 'edit',
    specific: true,
  },
  'self-managed': {
    area: 'manager',
    summary: 'They’re listed as their own manager and could approve their own time off.',
    fix: 'Pick a different manager in the employee record.',
    fixKind: 'edit',
  },

  // Jr manager assignment
  'jr-ae-link-broken': {
    area: 'jr-ae',
    summary: 'The assigned Jr manager account no longer exists.',
    fix: 'Re-select or clear the Jr manager in the employee record.',
    fixKind: 'edit',
  },
  'jr-ae-wrong-role': {
    area: 'jr-ae',
    summary: 'The assigned user doesn’t have the Jr manager role, so scoped pages won’t open for them.',
    fix: `Grant the Jr manager role in ${ACCESS_TAB_LABEL}, or clear the assignment in the employee record.`,
    fixKind: 'access',
    specific: true,
  },
  'jr-ae-inactive': {
    area: 'jr-ae',
    summary: 'The assigned Jr manager is no longer active.',
    fix: 'Reassign or clear the Jr manager in the employee record.',
    fixKind: 'edit',
    specific: true,
  },
  'self-jr-ae': {
    area: 'jr-ae',
    summary: 'They’re assigned as their own Jr manager and could approve their own time off.',
    fix: 'Pick a different Jr manager or clear the assignment in the employee record.',
    fixKind: 'edit',
  },

  // Logins
  'auth-unlinked': {
    area: 'login',
    summary: 'A login exists for their email but isn’t linked to the record.',
    fix: 'Sync manager links to connect it.',
    fixKind: 'sync',
    specific: true,
  },
  'no-auth-user': {
    area: 'login',
    summary: 'No login is linked, so they can’t sign in to request time off or see balances.',
    fix: `Create their login in ${ACCESS_TAB_LABEL}, then sync manager links to connect it.`,
    fixKind: 'access',
  },
  'auth-link-broken': {
    area: 'login',
    summary: 'The linked login account no longer exists.',
    fix: 'Re-select their login in the employee record.',
    fixKind: 'edit',
  },
  'auth-shared': {
    area: 'login',
    summary: 'Another active record is linked to the same login, so time off can attach to the wrong record.',
    fix: 'Link each record to its own login in the employee record.',
    fixKind: 'edit',
  },

  // Time-off allotments
  'no-allotments': {
    area: 'allotments',
    summary: 'PTO and sick time are both 0 days, so balances show empty and requests draw from nothing.',
    fix: 'Set their allotments in the employee record.',
    fixKind: 'edit',
  },
  'no-pto': {
    area: 'allotments',
    summary: 'PTO allotment is 0 days, so their PTO balance shows empty.',
    fix: 'Set their PTO in the employee record, unless 0 days is intended.',
    fixKind: 'edit',
  },
  'no-sick': {
    area: 'allotments',
    summary: 'Sick allotment is 0 days, so their sick balance shows empty.',
    fix: 'Set their sick time in the employee record, unless 0 days is intended.',
    fixKind: 'edit',
  },
  'no-start-date': {
    area: 'allotments',
    summary: 'Without a start date the tenure policy can’t calculate PTO, so their allotments stay frozen.',
    fix: 'Add their start date in the employee record.',
    fixKind: 'edit',
  },

  // WFH escalation
  'no-vp': {
    area: 'wfh',
    summary: 'They aren’t on a hybrid schedule, so WFH requests need a VP. With none set, requests go to the default escalation address.',
    fix: 'Set their VP in the employee record.',
    fixKind: 'edit',
  },
  'vp-unrecognized': {
    area: 'wfh',
    summary: 'The VP name doesn’t match any employee, so WFH escalations go to the default address.',
    fix: 'Correct the VP name in the employee record.',
    fixKind: 'edit',
    specific: true,
  },
  'vp-ambiguous': {
    area: 'wfh',
    summary: 'Several employees share the VP’s name, so WFH escalations go to the default address.',
    fix: 'Make the VP’s name unique, then re-select the VP in the employee record.',
    fixKind: 'edit',
    specific: true,
  },
  'vp-name-inexact': {
    area: 'wfh',
    summary: 'The VP name differs in case or spacing, so WFH escalations go to the default address.',
    fix: 'Fix the spelling to match the VP’s record exactly.',
    fixKind: 'edit',
    specific: true,
  },
  'vp-inactive': {
    area: 'wfh',
    summary: 'Their VP is no longer active, so WFH escalations go to the fallback recipient.',
    fix: 'Assign a current VP in the employee record.',
    fixKind: 'edit',
    specific: true,
  },
};

/** The guide for an issue; unknown keys fall back to their own detail and an edit fix. */
export const guideFor = (issue: SetupIssue): IssueGuide =>
  ISSUE_GUIDE[issue.key] ?? {
    area: 'records',
    summary: issue.detail,
    fix: 'Open the employee record to fix it.',
    fixKind: 'edit',
  };

/**
 * The first sentence of an issue's detail: the specific fact ("Matched by
 * name only ("Evan Brown").") without the explanation the group already
 * shows. A capital letter before the period (a middle initial) never ends it.
 */
export const firstSentence = (text: string) => {
  const match = text.match(/^(.+?[^A-Z\s]\.)\s+(?=[A-Z"“])/);
  return match ? match[1] : text;
};

/* ------------------------------------------------------------------------ */
/* Grouping                                                                 */
/* ------------------------------------------------------------------------ */

export interface IssueGroupPerson {
  employee: Employee;
  issue: SetupIssue;
}

export interface IssueGroup {
  key: string;
  label: string;
  severity: IssueSeverity;
  guide: IssueGuide;
  people: IssueGroupPerson[];
}

const byName = (a: Employee, b: Employee) => (a.name || '').localeCompare(b.name || '');

/** One group per issue key, largest first; people inside sorted by name. */
export const groupByIssue = (audited: AuditedEmployee[]): IssueGroup[] => {
  const groups = new Map<string, IssueGroup>();
  audited.forEach(({ employee, issues }) => {
    issues.forEach((issue) => {
      let group = groups.get(issue.key);
      if (!group) {
        group = { key: issue.key, label: issue.label, severity: issue.severity, guide: guideFor(issue), people: [] };
        groups.set(issue.key, group);
      }
      group.people.push({ employee, issue });
    });
  });
  return Array.from(groups.values())
    .map((group) => ({ ...group, people: [...group.people].sort((a, b) => byName(a.employee, b.employee)) }))
    .sort((a, b) => b.people.length - a.people.length || a.label.localeCompare(b.label));
};

export interface AreaHealth {
  area: CheckArea;
  /** Employees with at least one critical issue in this area. */
  broken: number;
  /** Employees with only warnings in this area. */
  partial: number;
}

export const areaHealth = (flagged: AuditedEmployee[]): AreaHealth[] =>
  CHECK_AREAS.map((area) => {
    let broken = 0;
    let partial = 0;
    flagged.forEach(({ issues }) => {
      const areaIssues = issues.filter((issue) => guideFor(issue).area === area.key);
      if (areaIssues.length === 0) return;
      if (areaIssues.some((issue) => issue.severity === 'critical')) broken += 1;
      else partial += 1;
    });
    return { area, broken, partial };
  });

export const inArea = (item: AuditedEmployee, area: CheckAreaKey | null) =>
  area === null || item.issues.some((issue) => guideFor(issue).area === area);

/** True when "Sync manager links" would fix at least one flagged issue. */
export const hasSyncFix = (flagged: AuditedEmployee[]) =>
  flagged.some(({ issues }) => issues.some((issue) => guideFor(issue).fixKind === 'sync'));

/** Client-side search across the fields an admin would recognize a person by. */
export const matchesSearch = (employee: Employee, query: string) => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return [employee.name, employee.email, employee.department?.name, employee.teamName, employee.managerName]
    .some((value) => (value || '').toLowerCase().includes(needle));
};
